import { FC, useCallback, useMemo } from "react"
import { Link } from "gatsby"
import classNames from "classnames"

// components
import { EventbriteLogo } from "./EventbriteLogo"
import { ProfileMenuBar } from "../ProfileMenuBar/ProfileMenuBar"
import {
  HeaderSiteSearch,
  HeaderSiteSearchMicroCopy
} from "../HeaderSiteSearch/HeaderSiteSearch"
import { MicroCopy } from "../ContactForm/ContactForm"

// utils
import { events } from "../../utils/heap/tracking_events"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import { handleUrlString } from "../../utils/seo/seo_utils"
import { getBaseUrl } from "../../utils/general_utils"

// styles
import * as globalStyles from "../../theme/global.module.scss"
import * as styles from "./Header.module.scss"
import { Typography } from "@eventbrite/marmalade"

export type UpperNavMicroCopy = {
  navigationHomepage: MicroCopy
  navigationLogIn: MicroCopy
  navigationBrowseEvents: MicroCopy
  navigationTickets: MicroCopy
  navigationManageMyEvents: MicroCopy
  navigationAccountSettings: MicroCopy
  navigationLogOut: MicroCopy
}

export type HeaderMicroCopy = Queries.HomepageDataQuery["upperNavMicroCopySet"]

export type Props = {
  headerMicroCopy: HeaderMicroCopy
  headerSiteSearchMicroCopy: HeaderSiteSearchMicroCopy
  urlPath: string
  isSearchBarVisible?: boolean
  isResultsBoxShown?: boolean
}

const FALLBACK_MICROCOPY: HeaderMicroCopy = {
  microcopySetEntries: [
    {
      microcopyKey: "navigationHomepage",
      microcopyValue: {
        raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Help Center","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
      }
    },
    {
      microcopyKey: "navigationLogIn",
      microcopyValue: {
        raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Log in","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
      }
    },
    {
      microcopyKey: "navigationBrowseEvents",
      microcopyValue: {
        raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Browse events","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
      }
    },
    {
      microcopyKey: "navigationTickets",
      microcopyValue: {
        raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Tickets","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
      }
    },
    {
      microcopyKey: "navigationManageMyEvents",
      microcopyValue: {
        raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Manage my events","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
      }
    },
    {
      microcopyKey: "navigationAccountSettings",
      microcopyValue: {
        raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Account settings","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
      }
    },
    {
      microcopyKey: "navigationLogOut",
      microcopyValue: {
        raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Log out","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
      }
    }
  ]
}

export const Header: FC<Props> = ({
  headerSiteSearchMicroCopy,
  urlPath,
  headerMicroCopy = FALLBACK_MICROCOPY,
  isSearchBarVisible = false,
  isResultsBoxShown = true
}) => {
  // const trackClick = useCallback(() => {
  //   events?.homepageHeaderClickEvent(Header?.name)
  // }, [typeof window !== "undefined"])

  const upperNavMicroCopy = useMemo<UpperNavMicroCopy>(() => {
    const objMap = headerMicroCopy?.microcopySetEntries.map((entry) =>
      Object.values(entry)
    )
    return Object.fromEntries(objMap)
  }, [headerMicroCopy?.microcopySetEntries])

  return (
    <header
      className={classNames(
        globalStyles?.edsAlignSpaceBetween,
        styles?.mainHeader
      )}
    >
      <div className={styles?.logoLinkContainer}>
        <a
          href={"/"}
          target="_blank"
          data-heap-id="icon.global-header.logo-link"
        >
          <EventbriteLogo />
        </a>

        <Link
          to={handleUrlString(urlPath, { removePrefix: true })}
          data-heap-id="link.global-header.help-center"
        >
          <Typography
            as="p"
            variant="body-lg-bold"
            className={styles?.homepageText}
          >
            {renderRichText(
              upperNavMicroCopy?.navigationHomepage?.raw,
              customRendererToRemovePTag
            )}
          </Typography>
        </Link>
      </div>

      <div className={styles?.headerProfileContainer}>
        {isSearchBarVisible ? (
          <HeaderSiteSearch
            isResultsBoxShown={isResultsBoxShown}
            siteSearchMicroCopy={{
              headerSearchResults:
                headerSiteSearchMicroCopy?.headerSearchResults,
              messageSearchBar: headerSiteSearchMicroCopy?.messageSearchBar
            }}
          />
        ) : null}
        <div className={styles.profilePlaceholder}>
          <ProfileMenuBar microCopy={upperNavMicroCopy} />
        </div>
      </div>
    </header>
  )
}
